import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCcw, MapPin, AlertTriangle, CheckCircle2, Filter, Clock } from "lucide-react";
import { api, API_BASE, getToken } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** LiveAttendance — Manager grid of today's team attendance.
 * Polls /api/attendance/today-team every 60s. */
export default function LiveAttendancePage() {
  const [data, setData] = useState(null);
  const [zones, setZones] = useState([]);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [onlyNotPunched, setOnlyNotPunched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState({});
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: u }] = await Promise.all([
        api.get("/attendance/today-team"),
        api.get("/users", { params: { page_size: 200 } }),
      ]);
      setData(t);
      const m = {}; (u.items || []).forEach((x) => { m[x.id] = x; });
      setUsers(m);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/zones").then((r) => setZones(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const int = setInterval(() => load(), 60000);
    return () => clearInterval(int);
  }, [load]);

  // Live running clock for punched-in cards (updates every 30s)
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const items = useMemo(() => {
    let arr = (data?.items || []);
    if (zoneFilter !== "all") {
      arr = arr.filter((i) => {
        const u = users[i.user_id];
        return (u?.assigned_zone_ids || []).includes(zoneFilter);
      });
    }
    if (onlyFlagged) arr = arr.filter((i) => i.within_radius === false);
    if (onlyNotPunched) arr = arr.filter((i) => !i.punch_in_ts);
    return arr;
  }, [data, users, zoneFilter, onlyFlagged, onlyNotPunched]);

  return (
    <div className="space-y-4" data-testid="live-attendance-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Live attendance" }]}
        title="Live attendance"
        subtitle="Today's punched-in and not-punched-in field users across all zones."
        favorite={{ key: "admin.live-attendance", type: "screen", label: "Live attendance", route: "/live-attendance" }}
        testid="liveattendancepage-page-header"
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Live attendance</h1>
          <p className="text-sm text-neutral-500">
            Team status as of <span className="font-mono">{data?.date || "—"}</span> · auto-refreshes every 60s
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={zoneFilter} onValueChange={setZoneFilter}>
            <SelectTrigger className="w-40" data-testid="live-zone-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All zones</SelectItem>
              {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.code} · {z.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-neutral-600" data-testid="live-flagged-toggle">
            <Switch checked={onlyFlagged} onCheckedChange={setOnlyFlagged} /> Only flagged
          </label>
          <label className="flex items-center gap-2 text-xs text-neutral-600" data-testid="live-notpunched-toggle">
            <Switch checked={onlyNotPunched} onCheckedChange={setOnlyNotPunched} /> Only not-punched
          </label>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="live-refresh">
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-12 text-center text-neutral-400">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No team activity today
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="live-grid">
          {items.map((i) => <TeamTile key={i.user_id} item={i} user={users[i.user_id]} tick={tick} />)}
        </div>
      )}
    </div>
  );
}

function TeamTile({ item, user, tick }) {
  const punched = !!item.punch_in_ts;
  const flagged = item.within_radius === false;
  const running = useMemo(() => {
    if (!punched || item.check_out_ts) return null;
    try {
      const start = new Date(item.punch_in_ts).getTime();
      return Math.max(0, Math.floor((Date.now() - start) / 1000));
    } catch { return null; }
  }, [item, tick]);

  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  // server sends "/api/photos/<ref>?w=100"; the photo endpoint needs the bearer token, so
  // pass the bare ref to AuthImage instead of using the URL in a plain <img>.
  const selfieRef = item.selfie_thumb_url
    ? decodeURIComponent(item.selfie_thumb_url.replace(/^\/api(\/v1)?\/photos\//, "").replace(/\?.*$/, ""))
    : null;
  const initialsAvatar = (
    <div className="h-14 w-14 rounded-full tulsi-gradient text-white flex items-center justify-center text-sm font-bold">
      {initials}
    </div>
  );

  return (
    <Card className={`border rounded-2xl overflow-hidden transition-shadow ${
      flagged ? "border-amber-300" : punched ? "border-green-200" : "border-neutral-200"
    }`} data-testid={`live-tile-${item.user_id.slice(0, 8)}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {selfieRef ? (
            <AuthImage refId={selfieRef} w={100} alt="" fallback={initialsAvatar}
                       className="h-14 w-14 rounded-full object-cover border border-neutral-200" />
          ) : initialsAvatar}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase text-neutral-400 tracking-widest">{item.role_code}</div>
            <div className="font-semibold text-neutral-900 truncate">{item.name || item.user_id}</div>
            <div className="mt-1">
              <StatusBadge status={item.status} punched={punched} flagged={flagged} />
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-neutral-500 space-y-1">
          {punched ? (
            <>
              <div>
                Started <span className="font-medium text-neutral-800">
                  {new Date(item.punch_in_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                {running !== null && (
                  <> · <span className="font-mono">{Math.floor(running / 3600)}h {Math.floor((running % 3600) / 60)}m</span> running</>
                )}
                {item.check_out_ts && (
                  <> → {new Date(item.check_out_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
                )}
              </div>
              {item.punch_in_gps && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="font-mono">
                    {item.punch_in_gps.lat?.toFixed(4)}, {item.punch_in_gps.lng?.toFixed(4)}
                  </span>
                  {flagged && (
                    <span className="ml-1 text-amber-700 inline-flex items-center gap-0.5">
                      <AlertTriangle className="h-3 w-3" /> flagged
                    </span>
                  )}
                  {item.within_radius === true && (
                    <span className="ml-1 text-green-700 inline-flex items-center gap-0.5">
                      <CheckCircle2 className="h-3 w-3" /> in-radius
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-neutral-400 italic">Not punched in yet</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, punched, flagged }) {
  // "flagged_radius" means the person DID punch in (they're present) — the
  // flag is only that their check-in GPS was outside the allowed geofence.
  // A bare "Flagged radius" label lost that "they're actually present" fact
  // and just read as an unexplained warning.
  const label = status === "absent" ? "Not punched"
    : status === "flagged_radius" ? "Present - Flagged Radius"
    : status.replace(/_/g, " ");
  const cls = flagged ? "bg-amber-100 text-amber-800" :
    status === "present" ? "bg-green-100 text-green-800" :
    status === "partial" ? "bg-orange-100 text-orange-700" :
    status === "flagged_radius" ? "bg-amber-100 text-amber-800" :
    "bg-neutral-100 text-neutral-500";
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>;
}
